import { useState } from 'react';
import {
  Box,
  Chip,
  Alert,
  Stack,
  Button,
  Tooltip,
  Typography,
  IconButton,
  CircularProgress
} from '@material-ui/core';
import {
  AutoFixHigh as RemoveWatermarkIcon,
  CheckCircle as CheckIcon,
  ZoomIn as ZoomInIcon
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { Image } from 'components/Image';
import useWebotServer from 'hooks/useWebotServer';
import { useListingWatermarkInfo } from './useListingWatermarkInfo';
import { WatermarkComparisonDialog } from './WatermarkComparisonDialog';
import type { WatermarkComparisonImage } from './WatermarkComparisonDialog';
import { WatermarkTermsCheckbox, useWatermarkTermsAccepted } from './WatermarkTermsCheckbox';

type WatermarkRemovalStudioProps = {
  listingId: string;
};

/**
 * Realtor-facing editor for removing Yad2/Madlan watermarks and realtor
 * branding (logos, phone overlays, portrait photos) from a listing's images.
 *
 * - Always shows the CLEAN versions (originals fall back for images without
 *   one). There is deliberately NO show/hide toggle (Haim reject 2026-08):
 *   removing a watermark means the clean image simply IS the image from then
 *   on; seeing the original is what the before/after comparison dialog is for.
 * - Clicking ANY image opens the full-size viewer — cleaned images get the
 *   before/after wipe slider, un-cleaned ones can be cleaned right from the
 *   viewer (async — keep paging and queue several in parallel).
 * - Clean versions live on the listing document itself (`watermarkFreeImages`)
 *   and are shared across all users — if someone already cleaned an image the
 *   server answers instantly with the existing version (no generation).
 */
export function WatermarkRemovalStudio({ listingId }: WatermarkRemovalStudioProps): JSX.Element {
  const { t: translate } = useTranslation();
  const { removeListingWatermarks } = useWebotServer();

  const { loading, imageUrls, cleanBySource, cleanedCount } = useListingWatermarkInfo(listingId);

  // Rights acknowledgment — rendered before every run, pre-checked once accepted
  // (the acceptance itself is persisted on the user doc as proof). Gates every
  // removal trigger in this studio, including the comparison dialog's buttons.
  const terms = useWatermarkTermsAccepted();

  const [busyUrls, setBusyUrls] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [compareIndex, setCompareIndex] = useState<number | null>(null);

  const allCleaned = imageUrls.length > 0 && cleanedCount === imageUrls.length;
  const isBusy = busyUrls.size > 0;

  // Every listing image, in gallery order, for the full-size viewer.
  const comparisonImages: WatermarkComparisonImage[] = imageUrls.map((url) => ({
    sourceUrl: url,
    cleanUrl: cleanBySource.get(url)
  }));

  const handleRemove = async (urls: string[], opts?: { force?: boolean }): Promise<void> => {
    // Belt to the buttons' suspenders: the comparison dialog also routes here, so the
    // terms gate must live on the single choke point, not only on visible buttons.
    if (!terms.accepted) {
      setError(
        translate(
          'watermarkRemoval.termsRequired',
          'כדי להסיר סימני מים יש לאשר תחילה שברשותך הזכויות לכך (הסימון למעלה)'
        )
      );
      return;
    }
    // `force` = explicit retry: regenerate even though a clean version exists (it replaces it).
    const pending = urls.filter(
      (url) => !busyUrls.has(url) && (opts?.force || !cleanBySource.has(url))
    );
    if (!pending.length) return;

    setError(null);
    setBusyUrls((prev) => new Set([...prev, ...pending]));
    try {
      const response = await removeListingWatermarks({
        listingId,
        imageUrls: pending,
        ...(opts?.force && { forceRegenerate: true })
      });
      const failures = response.results?.filter((r) => r.status === 'failed') ?? [];
      if (!response.success) {
        setError(
          response.errorMessage ??
            translate(
              'watermarkRemoval.partialFailure',
              'הסרת סימני המים נכשלה עבור {{count}} תמונות — אפשר לנסות שוב',
              { count: failures.length }
            )
        );
      }
    } catch (e) {
      console.error('WatermarkRemovalStudio - removeListingWatermarks failed', e);
      setError(translate('watermarkRemoval.requestFailed', 'הסרת סימני המים נכשלה — נסו שוב'));
    } finally {
      setBusyUrls((prev) => {
        const next = new Set(prev);
        pending.forEach((url) => next.delete(url));
        return next;
      });
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!imageUrls.length) {
    return (
      <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
        {translate('watermarkRemoval.noImages', 'אין תמונות לנכס זה')}
      </Typography>
    );
  }

  return (
    <Stack gap={2}>
      <WatermarkComparisonDialog
        open={compareIndex !== null}
        images={comparisonImages}
        index={compareIndex ?? 0}
        busyUrls={busyUrls}
        onClose={() => setCompareIndex(null)}
        onIndexChange={setCompareIndex}
        onRemove={(sourceUrl) => void handleRemove([sourceUrl])}
        onRetry={(sourceUrl) => void handleRemove([sourceUrl], { force: true })}
      />

      <Typography variant="body2" color="text.secondary">
        {translate(
          'watermarkRemoval.description',
          'הסרה של סימני מים, לוגואים, טלפונים ותמונות מתווך מתמונות הנכס בעזרת AI. ההסרה שייכת לנכס, לא ללקוח — אחרי שמסירים, הנכס מוצג נקי בכל המיניסייטים שלכם, גם בקישורים ששלחתם כבר.'
        )}
      </Typography>

      {/* Shown before EVERY removal (pre-checked once accepted — the acceptance is
          persisted on the user doc as proof of agreement to the watermark terms).
          Rendered even when all images are clean: the regenerate/retry path is an
          operation too, and the gate error points the user at this checkbox. */}
      <WatermarkTermsCheckbox {...terms} />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={1.5}
      >
        <Chip
          size="small"
          color={allCleaned ? 'success' : cleanedCount ? 'info' : 'default'}
          icon={cleanedCount ? <CheckIcon /> : undefined}
          label={
            allCleaned
              ? translate('watermarkRemoval.statusAll', 'כל התמונות ללא סימני מים')
              : translate(
                  'watermarkRemoval.statusSome',
                  '{{cleaned}} מתוך {{total}} תמונות ללא סימני מים',
                  {
                    cleaned: cleanedCount,
                    total: imageUrls.length
                  }
                )
          }
        />
        <Button
          variant="contained"
          startIcon={
            isBusy ? <CircularProgress size={16} color="inherit" /> : <RemoveWatermarkIcon />
          }
          disabled={allCleaned || isBusy || !terms.accepted}
          onClick={() => handleRemove(imageUrls)}
        >
          {translate('watermarkRemoval.removeAll', 'הסר סימני מים מכל התמונות')}
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Typography variant="caption" color="text.secondary">
        {translate(
          'watermarkRemoval.compareHint',
          'לחצו על תמונה כדי לפתוח אותה בגדול — כולל השוואת לפני / אחרי והסרת סימני מים תמונה-תמונה'
        )}
      </Typography>

      <Stack direction="row" flexWrap="wrap" gap={2} justifyContent="center">
        {imageUrls.map((url, index) => {
          const cleanUrl = cleanBySource.get(url);
          const isImageBusy = busyUrls.has(url);
          // Clean version always wins — no display toggle; the comparison dialog
          // is where the original can still be seen (before/after slider).
          const displayedUrl = cleanUrl ?? url;

          return (
            <Box
              key={url}
              sx={{ position: 'relative', cursor: 'zoom-in' }}
              onClick={() => setCompareIndex(index)}
            >
              {/* key remounts the lazy image when the shown URL flips original↔clean —
                  LazyLoadImage doesn't re-fire afterLoad on an in-place src swap, which
                  left the grey skeleton stuck over the updated image. */}
              <Image
                key={displayedUrl}
                sx={{
                  width: 168,
                  height: 168,
                  borderRadius: 1,
                  transition: 'all 200ms',
                  ...(isImageBusy && { opacity: 0.5 })
                }}
                src={displayedUrl}
                alt=""
                cropTopImage
              />
              {cleanUrl && (
                <Tooltip
                  title={translate('watermarkRemoval.imageCleaned', 'קיימת גרסה ללא סימני מים')}
                >
                  <CheckIcon
                    color="success"
                    sx={{
                      m: 0.5,
                      top: 0,
                      left: 0,
                      position: 'absolute',
                      bgcolor: 'white',
                      borderRadius: '50%'
                    }}
                  />
                </Tooltip>
              )}
              <Tooltip title={translate('watermarkRemoval.openLarge', 'פתיחה בגדול')}>
                <IconButton
                  size="small"
                  sx={{
                    m: 0.5,
                    bottom: 0,
                    right: 0,
                    color: 'white',
                    position: 'absolute',
                    bgcolor: 'rgba(0, 0, 0, 0.4)',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.6)'
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCompareIndex(index);
                  }}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {isImageBusy && (
                <CircularProgress
                  size={32}
                  sx={{
                    position: 'absolute',
                    top: 'calc(50% - 16px)',
                    left: 'calc(50% - 16px)'
                  }}
                />
              )}
              {!cleanUrl && !isImageBusy && (
                <Tooltip
                  title={
                    terms.accepted
                      ? translate('watermarkRemoval.removeSingle', 'הסר סימני מים מתמונה זו')
                      : translate(
                          'watermarkRemoval.termsRequiredShort',
                          'יש לאשר תחילה את הסימון למעלה'
                        )
                  }
                >
                  {/* span keeps the tooltip working while the button is disabled;
                      Box+sx (not inline style) so RTL flipping matches its siblings */}
                  <Box
                    component="span"
                    sx={{ m: 1, top: 0, right: 0, position: 'absolute' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconButton
                      disabled={!terms.accepted}
                      sx={{
                        color: 'white',
                        border: 1,
                        bgcolor: 'rgba(0, 0, 0, 0.4)',
                        borderColor: 'divider',
                        '&:hover': {
                          bgcolor: 'rgba(0, 0, 0, 0.6)'
                        },
                        '&.Mui-disabled': {
                          color: 'rgba(255, 255, 255, 0.4)',
                          bgcolor: 'rgba(0, 0, 0, 0.25)'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleRemove([url]);
                      }}
                    >
                      <RemoveWatermarkIcon />
                    </IconButton>
                  </Box>
                </Tooltip>
              )}
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}
